/**
 * Framework-agnostic Junction agent chat transport (SSE).
 * Hosts (AgentChat UI, ticket desk) own placement of text and errors.
 *
 * @param {object} options
 * @param {string} options.endpoint
 * @param {string} [options.abortEndpoint]
 * @param {() => string} [options.csrfToken]
 * @param {() => ({ agent?: string, provider?: string, model?: string, conversationId?: string|null })} [options.getPins]
 * @param {() => boolean|undefined} [options.getRemember]
 * @param {() => boolean|undefined} [options.getCompleteInBackground]
 * @param {() => (object|undefined)} [options.getClientContext]
 * @param {(delta: string) => void} [options.onDelta]
 * @param {(payload: { content: string, conversationId: string|null, finishReason: string|null, usage: object|null }) => void} [options.onComplete]
 * @param {(payload: { message: string, httpStatus: number|null, errorCode: string|null, providerError: string|null }) => void} [options.onError]
 * @param {(status: string) => void} [options.onStatus]
 * @param {number} [options.timeoutMs]
 */
export function createAgentChatClient(options = {}) {
    const endpoint = options.endpoint || '';
    const abortEndpoint = options.abortEndpoint || '';
    const csrfToken = typeof options.csrfToken === 'function'
        ? options.csrfToken
        : () => {
            const meta = document.head?.querySelector('meta[name="csrf-token"]');
            return meta ? meta.getAttribute('content') : '';
        };
    const getPins = typeof options.getPins === 'function' ? options.getPins : () => ({});
    const getRemember = typeof options.getRemember === 'function' ? options.getRemember : () => true;
    const getCompleteInBackground = typeof options.getCompleteInBackground === 'function'
        ? options.getCompleteInBackground
        : () => false;
    const getClientContext = typeof options.getClientContext === 'function'
        ? options.getClientContext
        : () => ({});
    const onDelta = options.onDelta || (() => {});
    const onComplete = options.onComplete || (() => {});
    const onError = options.onError || (() => {});
    const onStatus = options.onStatus || (() => {});
    const timeoutMs = options.timeoutMs ?? 120000;

    let controller = null;
    let timeoutId = null;
    let status = 'ready';
    let streamingContent = '';
    let conversationId = null;
    let finishReason = null;
    let usage = null;
    let lastError = null;
    let activeClientRequestId = null;
    let stopRequested = false;

    function setStatus(next) {
        status = next;
        onStatus(next);
    }

    function clearTimeoutGuard() {
        if (timeoutId) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
        }
    }

    function bumpTimeoutGuard() {
        clearTimeoutGuard();
        timeoutId = window.setTimeout(() => {
            if (controller) {
                controller.abort();
            }
            emitError({
                message: 'La risposta ha impiegato troppo tempo ed è stata interrotta.',
                httpStatus: null,
                errorCode: 'timeout',
                providerError: null,
            });
        }, timeoutMs);
    }

    function emitError(payload) {
        lastError = payload;
        setStatus('error');
        onError(payload);
    }

    function mapErrorCode(httpStatus) {
        if (httpStatus === 401 || httpStatus === 403) {
            return 'provider_auth';
        }
        if (httpStatus === 429) {
            return 'provider_rate_limit';
        }
        if (typeof httpStatus === 'number' && httpStatus >= 500) {
            return 'provider_upstream';
        }

        return httpStatus != null ? 'provider_error' : 'chat_error';
    }

    function handleEvent(rawEvent) {
        const line = rawEvent
            .split('\n')
            .find((l) => l.startsWith('data:'));

        if (!line) {
            return;
        }

        const payload = line.slice(5).trim();

        if (payload === '' || payload === '[DONE]') {
            return;
        }

        let data;
        try {
            data = JSON.parse(payload);
        } catch (e) {
            return;
        }

        if (typeof data.delta === 'string') {
            if (status !== 'streaming') {
                setStatus('streaming');
            }
            streamingContent += data.delta;
            onDelta(data.delta);
            bumpTimeoutGuard();
        }

        if (typeof data.conversation_id === 'string' && data.conversation_id !== '') {
            conversationId = data.conversation_id;
        }

        if (typeof data.error === 'string' && data.error !== '') {
            const httpStatus = typeof data.http_status === 'number' ? data.http_status : null;
            emitError({
                message: data.error,
                httpStatus,
                errorCode: typeof data.error_code === 'string' ? data.error_code : mapErrorCode(httpStatus),
                providerError: typeof data.provider_error === 'string' ? data.provider_error : null,
            });
        }

        if (data.finish_reason) {
            finishReason = data.finish_reason;
            usage = data.usage || null;

            if (data.finish_reason === 'error' && status !== 'error') {
                const httpStatus = typeof data.http_status === 'number' ? data.http_status : null;
                emitError({
                    message: typeof data.error === 'string' && data.error !== ''
                        ? data.error
                        : 'Si è verificato un errore durante la chat. Riprova.',
                    httpStatus,
                    errorCode: typeof data.error_code === 'string' ? data.error_code : mapErrorCode(httpStatus),
                    providerError: typeof data.provider_error === 'string' ? data.provider_error : null,
                });
            }
        }
    }

    async function consumeStream(body) {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
            const { value, done } = await reader.read();
            if (done) {
                break;
            }

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() || '';

            for (const part of parts) {
                handleEvent(part);
            }
        }

        if (buffer.trim()) {
            handleEvent(buffer);
        }
    }

    function commit() {
        if (status === 'error') {
            streamingContent = '';
            return;
        }

        if (stopRequested) {
            streamingContent = '';
            setStatus('ready');
            stopRequested = false;
            activeClientRequestId = null;
            return;
        }

        const content = streamingContent;
        streamingContent = '';
        setStatus('ready');
        onComplete({
            content,
            conversationId,
            finishReason,
            usage,
        });
        activeClientRequestId = null;
    }

    function newClientRequestId() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    /**
     * @param {string} prompt
     * @param {object} [overrides]
     * @param {Array<{role: string, content: string}>} [overrides.messages]
     * @param {boolean} [overrides.remember]
     * @param {boolean} [overrides.completeInBackground]
     * @param {object} [overrides.clientContext]
     */
    async function send(prompt, overrides = {}) {
        if (status !== 'ready' && status !== 'error') {
            return;
        }

        const content = typeof prompt === 'string' ? prompt.trim() : '';
        const messages = Array.isArray(overrides.messages)
            ? overrides.messages
            : (content ? [{ role: 'user', content }] : []);

        if (!messages.length) {
            return;
        }

        streamingContent = '';
        finishReason = null;
        usage = null;
        lastError = null;
        stopRequested = false;
        activeClientRequestId = newClientRequestId();
        setStatus('submitted');

        controller = new AbortController();
        bumpTimeoutGuard();

        try {
            const pins = getPins() || {};
            const payload = { messages };

            if (pins.provider) {
                payload.provider = pins.provider;
            }
            if (pins.model) {
                payload.model = pins.model;
            }
            if (pins.agent) {
                payload.agent = pins.agent;
            }
            if (pins.conversationId || conversationId) {
                payload.conversation_id = pins.conversationId || conversationId;
            }

            const remember = overrides.remember !== undefined
                ? overrides.remember
                : getRemember();
            if (remember === false) {
                payload.remember = false;
            }

            const completeInBackground = overrides.completeInBackground !== undefined
                ? overrides.completeInBackground
                : getCompleteInBackground();
            if (completeInBackground) {
                payload.complete_in_background = true;
                const context = overrides.clientContext !== undefined
                    ? overrides.clientContext
                    : getClientContext();
                if (context && typeof context === 'object') {
                    payload.client_context = context;
                }
            }

            if (activeClientRequestId) {
                payload.client_request_id = activeClientRequestId;
            }

            if (overrides.useMcpTools === false) {
                payload.use_mcp_tools = false;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    'X-CSRF-TOKEN': csrfToken(),
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            if (!response.ok || !response.body) {
                const httpStatus = response.status;
                const authHint = httpStatus === 401 || httpStatus === 403
                    ? ' Chiave API provider mancante o non valida, oppure permessi insufficienti.'
                    : '';
                emitError({
                    message: `Request failed (${httpStatus}).${authHint}`,
                    httpStatus,
                    errorCode: mapErrorCode(httpStatus),
                    providerError: null,
                });
                return;
            }

            await consumeStream(response.body);
            commit();
        } catch (err) {
            if (err && err.name === 'AbortError') {
                if (status !== 'error') {
                    commit();
                }
            } else if (status !== 'error') {
                emitError({
                    message: 'Si è verificato un errore durante la chat. Riprova.',
                    httpStatus: null,
                    errorCode: 'chat_error',
                    providerError: null,
                });
            }
        } finally {
            clearTimeoutGuard();
            controller = null;
        }
    }

    async function stop() {
        stopRequested = true;
        const requestId = activeClientRequestId;
        if (requestId && abortEndpoint) {
            try {
                await fetch(abortEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrfToken(),
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({ client_request_id: requestId }),
                    keepalive: true,
                });
            } catch (e) {
                // Best-effort Stop signal; still abort the local stream.
            }
        }
        cancel();
    }

    function cancel() {
        if (controller) {
            controller.abort();
        }
    }

    function resetThread() {
        cancel();
        conversationId = null;
        streamingContent = '';
        finishReason = null;
        usage = null;
        lastError = null;
        stopRequested = false;
        activeClientRequestId = null;
        setStatus('ready');
    }

    return {
        send,
        stop,
        cancel,
        resetThread,
        getStatus: () => status,
        getConversationId: () => conversationId,
        getStreamingContent: () => streamingContent,
        getLastError: () => lastError,
        setConversationId: (id) => {
            conversationId = id;
        },
    };
}

